/**
 * Customer Information Cards Component
 * Displays customer and boat information as read-only cards with edit capability
 */

/**
 * Normalize boat object to ensure consistent field access
 * Handles variations: boat_name/name, boat_length/length, boat_id/id, etc.
 */
function normalizeBoatObject(boat) {
    if (!boat) return null;

    return {
        // Canonical fields (matching database schema)
        id: boat.id || boat.boat_id,
        name: boat.name || boat.boat_name,
        length: boat.length || boat.boat_length || boat.boat_length_ft,
        type: normalizeBoatType(boat.type || boat.boat_type),
        hull_type: boat.hull_type,
        propeller_count: boat.propeller_count,
        marina: boat.marina || boat.marina_location,
        dock: boat.dock,
        slip: boat.slip || boat.slip_number,
        make: boat.make,
        model: boat.model
    };
}

/**
 * Normalize boat type to standard format
 * Database stores 'sailboat'/'powerboat'
 */
function normalizeBoatType(type) {
    if (!type) return null;
    const normalized = type.toLowerCase();
    // Accept both formats, return standard format
    if (normalized === 'sail' || normalized === 'sailboat') return 'sailboat';
    if (normalized === 'power' || normalized === 'powerboat') return 'powerboat';
    return type; // Return as-is if unknown
}

/**
 * Show customer and boat info cards
 */
export function showCustomerInfoCards(customer, boat) {
    const container = document.getElementById('customerInfoCards');
    if (!container) {
        console.error('customerInfoCards container not found');
        return;
    }

    container.innerHTML = `
        ${renderCustomerCard(customer)}
        ${boat ? renderBoatCard(boat) : ''}
    `;

    container.style.display = 'block';

    // Store data globally for access by other modules
    window.selectedCustomer = customer;
    window.selectedBoat = boat;

    console.log('✅ Customer info cards displayed');
}

/**
 * Render customer information card
 */
function renderCustomerCard(customer) {
    return `
        <div class="info-card customer-card" id="customerCard">
            <div class="card-header">
                <h3>👤 Customer Information</h3>
                <button class="edit-card-btn" onclick="window.enableEditMode('customer')" id="editCustomerBtn">
                    Edit
                </button>
            </div>
            <div class="card-content" id="customerCardContent">
                <div class="info-row">
                    <span class="info-label">Name:</span>
                    <span class="info-value" id="customerName">${customer.name || 'N/A'}</span>
                </div>
                <div class="info-row">
                    <span class="info-label">Email:</span>
                    <span class="info-value" id="customerEmail">${customer.email || 'N/A'}</span>
                </div>
                <div class="info-row">
                    <span class="info-label">Phone:</span>
                    <span class="info-value" id="customerPhone">${customer.phone || 'N/A'}</span>
                </div>
            </div>
            <div class="card-edit-form" id="customerEditForm" style="display: none;">
                <div class="form-group">
                    <label>Name</label>
                    <input type="text" id="editCustomerName" value="${customer.name || ''}" />
                </div>
                <div class="form-group">
                    <label>Email</label>
                    <input type="email" id="editCustomerEmail" value="${customer.email || ''}" />
                </div>
                <div class="form-group">
                    <label>Phone</label>
                    <input type="tel" id="editCustomerPhone" value="${customer.phone || ''}" />
                </div>
                <div class="card-actions">
                    <button class="save-btn" onclick="window.saveCustomerChanges()">Save</button>
                    <button class="cancel-btn" onclick="window.cancelEdit('customer')">Cancel</button>
                </div>
            </div>
        </div>
    `;
}

/**
 * Render boat information card
 */
function renderBoatCard(boat) {
    // Normalize boat object for consistent field access
    const b = normalizeBoatObject(boat);
    if (!b) return '';

    // Get saved anodes for this boat
    const anodesSummary = formatAnodesSummary();

    console.log('📦 Rendering boat card with normalized data:', b);

    return `
        <div class="info-card boat-card" id="boatCard">
            <div class="card-header">
                <h3>🚤 Boat Information</h3>
                <button class="edit-card-btn" onclick="window.enableEditMode('boat')" id="editBoatBtn">
                    Edit
                </button>
            </div>
            <div class="card-content" id="boatCardContent">
                <div class="info-row">
                    <span class="info-label">Boat Name:</span>
                    <span class="info-value" id="boatName">${b.name || 'N/A'}</span>
                </div>
                <div class="info-row">
                    <span class="info-label">Length:</span>
                    <span class="info-value" id="boatLength">${b.length ? b.length + ' ft' : 'N/A'}</span>
                </div>
                <div class="info-row">
                    <span class="info-label">Location:</span>
                    <span class="info-value" id="boatLocation">${formatLocation(b)}</span>
                </div>
                <div class="info-row">
                    <span class="info-label">Type:</span>
                    <span class="info-value" id="boatType">${formatBoatType(b)}</span>
                </div>
                <div class="info-row">
                    <span class="info-label">Hull:</span>
                    <span class="info-value" id="hullType">${b.hull_type || 'N/A'}</span>
                </div>
                <div class="info-row">
                    <span class="info-label">Propellers:</span>
                    <span class="info-value" id="propCount">${formatPropellerCount(b)}</span>
                </div>
                ${anodesSummary ? `
                <div class="info-row anodes-row">
                    <span class="info-label">Known Anodes:</span>
                    <span class="info-value" id="boatAnodes">
                        <div class="anodes-summary">${anodesSummary}</div>
                    </span>
                </div>
                ` : ''}
            </div>
            <div class="card-edit-form" id="boatEditForm" style="display: none;">
                <div class="form-group">
                    <label>Boat Name</label>
                    <input type="text" id="editBoatName" value="${b.name || ''}" />
                </div>
                <div class="form-group">
                    <label>Length (ft)</label>
                    <input type="number" id="editBoatLength" value="${b.length || ''}" />
                </div>
                <div class="form-group">
                    <label>Dock</label>
                    <input type="text" id="editBoatDock" value="${b.dock || ''}" />
                </div>
                <div class="form-group">
                    <label>Slip</label>
                    <input type="text" id="editBoatSlip" value="${b.slip || ''}" />
                </div>
                <div class="form-group">
                    <label>Marina</label>
                    <input type="text" id="editBoatMarina" value="${b.marina || ''}" />
                </div>
                <div class="form-group">
                    <label>Type</label>
                    <select id="editBoatType">
                        <option value="sailboat" ${b.type === 'sailboat' ? 'selected' : ''}>Sailboat</option>
                        <option value="powerboat" ${b.type === 'powerboat' ? 'selected' : ''}>Powerboat</option>
                    </select>
                </div>
                <div class="form-group">
                    <label>Hull Type</label>
                    <select id="editHullType">
                        <option value="monohull" ${b.hull_type === 'monohull' ? 'selected' : ''}>Monohull</option>
                        <option value="catamaran" ${b.hull_type === 'catamaran' ? 'selected' : ''}>Catamaran</option>
                        <option value="trimaran" ${b.hull_type === 'trimaran' ? 'selected' : ''}>Trimaran</option>
                    </select>
                </div>
                <div class="form-group">
                    <label>Number of Propellers</label>
                    <select id="editPropellerCount">
                        <option value="0" ${(!b.propeller_count || b.propeller_count === 0) ? 'selected' : ''}>0 - No Propellers</option>
                        <option value="1" ${b.propeller_count === 1 ? 'selected' : ''}>1 - Single Propeller</option>
                        <option value="2" ${b.propeller_count === 2 ? 'selected' : ''}>2 - Twin Propellers</option>
                        <option value="3" ${b.propeller_count === 3 ? 'selected' : ''}>3 - Triple Propellers</option>
                        <option value="4" ${b.propeller_count === 4 ? 'selected' : ''}>4 - Quad Propellers</option>
                        <option value="5" ${b.propeller_count === 5 ? 'selected' : ''}>5 - Five Propellers</option>
                    </select>
                </div>
                <div class="card-actions">
                    <button class="save-btn" onclick="window.saveBoatChanges()">Save</button>
                    <button class="cancel-btn" onclick="window.cancelEdit('boat')">Cancel</button>
                </div>
            </div>
        </div>
    `;
}

/**
 * Format boat location from dock/slip/marina
 */
function formatLocation(boat) {
    const parts = [];
    const dock = boat.dock;
    const slip = boat.slip || boat.slip_number;
    const marina = boat.marina || boat.marina_location;

    if (dock) parts.push(`Dock ${dock}`);
    if (slip) parts.push(`Slip ${slip}`);
    if (marina) parts.push(marina);
    return parts.length > 0 ? parts.join(', ') : 'N/A';
}

/**
 * Format boat type display
 * Handles both database format ('sail'/'power') and UI format ('sailboat'/'powerboat')
 */
function formatBoatType(boat) {
    if (!boat.type) return 'N/A';

    // Handle both formats: 'sail'/'power' from DB and 'sailboat'/'powerboat' from UI
    const typeMap = {
        'sail': 'Sailboat',
        'power': 'Powerboat',
        'sailboat': 'Sailboat',
        'powerboat': 'Powerboat'
    };

    return typeMap[boat.type.toLowerCase()] ||
           boat.type.charAt(0).toUpperCase() + boat.type.slice(1);
}

/**
 * Format propeller count display
 */
function formatPropellerCount(boat) {
    if (boat.propeller_count === null || boat.propeller_count === undefined) {
        return 'N/A';
    }
    if (boat.propeller_count === 0) {
        return '0 Propellers';
    }
    if (boat.propeller_count === 1) {
        return '1 Propeller';
    }
    return `${boat.propeller_count} Propellers`;
}

/**
 * Format anodes summary from window.anodeAssignments
 */
function formatAnodesSummary() {
    if (!window.anodeAssignments || Object.keys(window.anodeAssignments).length === 0) {
        return null;
    }

    const anodesByLocation = {};

    // Group anodes by location
    Object.entries(window.anodeAssignments).forEach(([anodeId, assignment]) => {
        const location = assignment.location || 'Unknown';
        if (!anodesByLocation[location]) {
            anodesByLocation[location] = [];
        }
        anodesByLocation[location].push(assignment);
    });

    // Format as readable summary
    const summaryParts = Object.entries(anodesByLocation).map(([location, anodes]) => {
        const count = anodes.length;
        const locationName = location.charAt(0).toUpperCase() + location.slice(1);

        // Get anode name/model if available
        const anodeName = anodes[0]?.anode_name || 'Unknown';

        return `<div style="margin: 3px 0; font-size: 14px;">${count}x ${locationName} (${anodeName})</div>`;
    });

    return summaryParts.join('');
}

/**
 * Get condition label from numeric value
 * Handles both percentage values (0-100) and index values (0-4)
 */
function getConditionLabel(condition) {
    // If it's a percentage value (> 4), just return it as percentage
    if (condition > 4) {
        return `${condition}%`;
    }

    // Otherwise treat as index into labels array
    const labels = ['New', 'Good', 'Fair', 'Poor', 'Replace'];
    return labels[condition] || 'Unknown';
}

/**
 * Enable edit mode for a card
 */
export function enableEditMode(cardType) {
    const content = document.getElementById(`${cardType}CardContent`);
    const form = document.getElementById(`${cardType}EditForm`);
    const editBtn = document.getElementById(`edit${cardType.charAt(0).toUpperCase() + cardType.slice(1)}Btn`);

    if (content && form && editBtn) {
        content.style.display = 'none';
        form.style.display = 'block';
        editBtn.style.display = 'none';
    }
}

/**
 * Cancel edit mode
 */
export function cancelEdit(cardType) {
    const content = document.getElementById(`${cardType}CardContent`);
    const form = document.getElementById(`${cardType}EditForm`);
    const editBtn = document.getElementById(`edit${cardType.charAt(0).toUpperCase() + cardType.slice(1)}Btn`);

    if (content && form && editBtn) {
        content.style.display = 'block';
        form.style.display = 'none';
        editBtn.style.display = 'inline-block';
    }
}

/**
 * Save customer changes
 */
export async function saveCustomerChanges() {
    const name = document.getElementById('editCustomerName').value;
    const email = document.getElementById('editCustomerEmail').value;
    const phone = document.getElementById('editCustomerPhone').value;

    // Validate customer data
    if (!window.selectedCustomer || !window.selectedCustomer.id) {
        console.error('No customer selected or customer ID missing');
        alert('Error: No customer selected for editing');
        return;
    }

    const stripeCustomerId = window.selectedCustomer.id;

    // Save to Supabase customers table
    try {
        // First, check if customer exists in Supabase by stripe_customer_id
        const { data: existingCustomer, error: fetchError } = await window.supabaseClient
            .from('customers')
            .select('id')
            .eq('stripe_customer_id', stripeCustomerId)
            .single();

        if (fetchError && fetchError.code !== 'PGRST116') {
            console.error('Error checking customer:', fetchError);
            throw fetchError;
        }

        let supabaseResult;
        if (existingCustomer) {
            // Update existing customer
            supabaseResult = await window.supabaseClient
                .from('customers')
                .update({
                    name: name || null,
                    email: email || null,
                    phone: phone || null,
                    updated_at: new Date().toISOString()
                })
                .eq('stripe_customer_id', stripeCustomerId);
        } else {
            // Create new customer record
            supabaseResult = await window.supabaseClient
                .from('customers')
                .insert({
                    stripe_customer_id: stripeCustomerId,
                    name: name || null,
                    email: email || null,
                    phone: phone || null
                });
        }

        if (supabaseResult.error) {
            console.error('Error saving customer to Supabase:', supabaseResult.error);
            alert(`Failed to save customer changes: ${supabaseResult.error.message}`);
            return;
        }

        console.log('✅ Customer saved to Supabase successfully');

    } catch (err) {
        console.error('Unexpected error saving customer:', err);
        alert('An unexpected error occurred while saving customer changes');
        return;
    }

    // Update display
    document.getElementById('customerName').textContent = name || 'N/A';
    document.getElementById('customerEmail').textContent = email || 'N/A';
    document.getElementById('customerPhone').textContent = phone || 'N/A';

    // Update global state
    if (window.selectedCustomer) {
        window.selectedCustomer.name = name;
        window.selectedCustomer.email = email;
        window.selectedCustomer.phone = phone;
    }

    console.log('✅ Customer changes saved successfully');

    cancelEdit('customer');
}

/**
 * Save boat changes
 */
export async function saveBoatChanges() {
    const name = document.getElementById('editBoatName').value;
    const length = document.getElementById('editBoatLength').value;
    const dock = document.getElementById('editBoatDock').value;
    const slip = document.getElementById('editBoatSlip').value;
    const marina = document.getElementById('editBoatMarina').value;
    const type = document.getElementById('editBoatType').value;
    const hullType = document.getElementById('editHullType').value;
    const propellerCount = parseInt(document.getElementById('editPropellerCount').value);

    // Validate boat ID
    const boatId = window.selectedBoat?.boat_id || window.selectedBoat?.id;
    if (!window.selectedBoat || !boatId) {
        console.error('No boat selected or boat ID missing');
        alert('Error: No boat selected for editing');
        return;
    }

    console.log('Saving boat with ID:', boatId);

    // Save to Supabase boats table
    try {
        const lengthValue = parseFloat(length) || null;

        // Map type values: 'sailboat' -> 'sail', 'powerboat' -> 'power'
        const boatTypeValue = type === 'sailboat' ? 'sail' : type === 'powerboat' ? 'power' : null;

        const updateData = {
            name: name || null,
            boat_name: name || null,
            length: lengthValue,
            boat_length_ft: lengthValue,
            dock: dock || null,
            slip: slip || null,
            marina: marina || null,
            marina_location: marina || null,
            propeller_count: propellerCount,
            updated_at: new Date().toISOString()
        };

        // Only set type/boat_type if valid values
        if (type && (type === 'sailboat' || type === 'powerboat')) {
            updateData.type = type;
            updateData.boat_type = boatTypeValue;
        }

        // Only set hull_type if valid value
        if (hullType && ['monohull', 'catamaran', 'trimaran'].includes(hullType)) {
            updateData.hull_type = hullType;
        }

        const { data, error } = await window.supabaseClient
            .from('boats')
            .update(updateData)
            .eq('id', boatId)
            .select()
            .single();

        if (error) {
            console.error('Error saving boat to database:', error);
            alert(`Failed to save boat changes: ${error.message}`);
            return;
        }

        console.log('✅ Boat saved to database successfully:', data);

    } catch (err) {
        console.error('Unexpected error saving boat:', err);
        alert('An unexpected error occurred while saving boat changes');
        return;
    }

    // Update display
    document.getElementById('boatName').textContent = name || 'N/A';
    document.getElementById('boatLength').textContent = length ? length + ' ft' : 'N/A';
    document.getElementById('boatLocation').textContent = formatLocation({ dock, slip, marina });
    document.getElementById('boatType').textContent = formatBoatType({ type });
    document.getElementById('hullType').textContent = hullType || 'N/A';
    document.getElementById('propCount').textContent = formatPropellerCount({ propeller_count: propellerCount });

    // Update global state
    if (window.selectedBoat) {
        window.selectedBoat.name = name;
        window.selectedBoat.boat_name = name;
        window.selectedBoat.length = parseFloat(length);
        window.selectedBoat.boat_length_ft = parseFloat(length);
        window.selectedBoat.dock = dock;
        window.selectedBoat.slip = slip;
        window.selectedBoat.marina = marina;
        window.selectedBoat.marina_location = marina;
        window.selectedBoat.type = type;
        window.selectedBoat.boat_type = type;
        window.selectedBoat.hull_type = hullType;
        window.selectedBoat.propeller_count = propellerCount;
    }

    cancelEdit('boat');

    // Update pricing calculations
    console.log('🔄 Triggering pricing updates after boat changes');

    if (window.updateWizardPricing) {
        window.updateWizardPricing();
    }

    if (window.updateChargeSummary) {
        window.updateChargeSummary();
    }

    // Update propeller condition fields based on new propeller count
    if (window.updatePropellerConditionFields) {
        window.updatePropellerConditionFields();
    }
}

/**
 * Hide customer info cards
 */
export function hideCustomerInfoCards() {
    const container = document.getElementById('customerInfoCards');
    if (container) {
        container.style.display = 'none';
        container.innerHTML = '';
    }
}

/**
 * Refresh anodes display in boat card
 * Called after anodes are loaded or updated
 */
export function refreshAnodesDisplay() {
    const anodesSummary = formatAnodesSummary();

    // Try to find existing anodes container
    let anodesContainer = document.getElementById('boatAnodes');

    if (!anodesContainer) {
        // Container doesn't exist - create it if we have anodes to show
        if (anodesSummary) {
            console.log('📦 Anodes container not found - injecting new anodes row');

            // Find the boat card content
            const boatCardContent = document.getElementById('boatCardContent');
            if (boatCardContent) {
                // Create the anodes row
                const anodesRow = document.createElement('div');
                anodesRow.className = 'info-row anodes-row';
                anodesRow.innerHTML = `
                    <span class="info-label">Known Anodes:</span>
                    <span class="info-value" id="boatAnodes">
                        <div class="anodes-summary">${anodesSummary}</div>
                    </span>
                `;

                // Append to boat card content
                boatCardContent.appendChild(anodesRow);
                console.log('✅ Anodes row injected successfully');
            } else {
                console.warn('⚠️ Could not find boat card content to inject anodes');
            }
        } else {
            console.log('ℹ️  No anodes to display yet');
        }
        return;
    }

    // Container exists - update it
    if (anodesSummary) {
        // Update existing display
        anodesContainer.innerHTML = `<div class="anodes-summary">${anodesSummary}</div>`;
        console.log('✅ Anodes display updated');
    } else {
        // Remove the anodes row if no anodes
        const anodesRow = anodesContainer.closest('.anodes-row');
        if (anodesRow) {
            anodesRow.remove();
            console.log('🗑️  Anodes row removed (no anodes)');
        }
    }
}

// Expose functions to window
window.showCustomerInfoCards = showCustomerInfoCards;
window.enableEditMode = enableEditMode;
window.cancelEdit = cancelEdit;
window.saveCustomerChanges = saveCustomerChanges;
window.saveBoatChanges = saveBoatChanges;
window.hideCustomerInfoCards = hideCustomerInfoCards;
window.refreshAnodesDisplay = refreshAnodesDisplay;
